// Pure derivation for the zone occupancy badge (story #496), extracted so it can be tested with
// `bun test` without a DOM (the frontend has no RTL). Maps an OccupancyReadOut to the tone,
// label, and detail the badge renders — measured vs. inferred, with confidence or an honest
// "waiting" state on cold-start.
import type { OccupancyReadOut } from "@/lib/api/agent";
import type { CardTone } from "@/lib/card-styles";

export interface OccupancyBadgeView {
  tone: CardTone;
  label: string;
  detail: string | null;
  title: string;
}

// Shared 0.0-1.0 → "NN %" convention — confidence here, the inferred occupancy_probability
// wherever the read-out's value is rendered (e.g. ZoneTile's Sisäilmaolosuhteet card).
export function formatOccupancyPercent(fraction: number): string {
  return `${Math.round(fraction * 100)} %`;
}

// Sisäilmaolosuhteet zone tiles: inferred occupancy is one current-value row among
// CO₂ / temperature, not OccupancyBadge chrome. Absent when not ready (same as a
// missing CO₂ series), when the source is measured (headcount is occupancy_count),
// or when occupancy_count is already on the tile.
export interface OccupancySignalRow {
  label: string;
  value: string;
}

export function inferredOccupancySignalRow(
  occupancy: OccupancyReadOut | null,
  hasMeasuredHeadcount: boolean,
): OccupancySignalRow | null {
  if (hasMeasuredHeadcount) return null;
  if (
    occupancy == null ||
    occupancy.source !== "inferred" ||
    !occupancy.ready ||
    occupancy.value === null
  ) {
    return null;
  }
  return {
    label: "Käyttöaste",
    value: formatOccupancyPercent(occupancy.value),
  };
}

export function occupancyBadgeView(
  occupancy: OccupancyReadOut,
): OccupancyBadgeView {
  const measured = occupancy.source === "measured";
  const tone: CardTone = measured
    ? "ok"
    : occupancy.ready
      ? "learning"
      : "empty";
  const label = measured ? "Mitattu läsnäolo" : "Päätelty läsnäolo";

  let detail: string | null = null;
  if (measured) {
    detail = "anturi";
  } else if (!occupancy.ready) {
    detail = "odottaa oppimista";
  } else if (occupancy.confidence !== null) {
    detail = `luottamus ${formatOccupancyPercent(occupancy.confidence)}`;
  }

  const title = measured
    ? "Läsnäolo mitataan tälle vyöhykkeelle omalla anturilla"
    : occupancy.ready
      ? "Läsnäolo päätellään palvelevan laitteen käyttöastekaavasta ja vyöhykkeen proxy-signaaleista"
      : "Läsnäolon päättely odottaa palvelevan laitteen kaavan valmistumista";

  return { tone, label, detail, title };
}
